/**
 * Performance Monitoring Utility
 *
 * Tracks and reports performance metrics for the application
 */

import { isDevelopment } from './environment';
import { generateUuid } from './uuid';

/**
 * Layout Instability API — `layout-shift` entries (CLS). Some `lib.dom` versions omit this interface.
 * @see https://w3c.github.io/layout-instability/#sec-layout-shift
 */
interface LayoutShiftEntry extends PerformanceEntry {
  readonly value: number;
  readonly hadRecentInput: boolean;
}

function isLayoutShiftEntry(entry: PerformanceEntry): entry is LayoutShiftEntry {
  if (entry.entryType !== 'layout-shift') return false;
  if (!('value' in entry) || !('hadRecentInput' in entry)) return false;
  const { value, hadRecentInput } = entry as { value: unknown; hadRecentInput: unknown };
  return typeof value === 'number' && typeof hadRecentInput === 'boolean';
}

function isLargestContentfulPaintEntry(entry: PerformanceEntry): entry is LargestContentfulPaint {
  return entry.entryType === 'largest-contentful-paint';
}

function isFirstInputEntry(entry: PerformanceEntry): entry is PerformanceEventTiming {
  if (entry.entryType !== 'first-input') return false;
  return typeof (entry as PerformanceEventTiming).processingStart === 'number';
}

/** Stable grep target when simulated metric jitter falls back from Web Crypto (dev-only log, once per load). */
const PERF_SIM_RANDOM_CRYPTO_FALLBACK = 'PERF_SIM_RANDOM_CRYPTO_FALLBACK';

let perfSimRandomCryptoFallbackLogged = false;
let perfSimFallbackMixCounter = 0;

/**
 * [0, 1) when `getRandomValues` cannot run: mixes wall time, high-resolution time, and a call counter.
 * **Not** suitable for secrets, session IDs, or unpredictability against an adversary — only for
 * non-sensitive simulated dashboard metrics (see `randomUnitForSimulation`). Avoids `Math.random`
 * so this path is not mistaken for a security-sensitive PRNG use.
 */
function weakNonCryptoUnitForSimulationFallback(): number {
  perfSimFallbackMixCounter = Math.trunc(perfSimFallbackMixCounter + 1);
  const t = Date.now();
  const hiResSubMs =
    typeof performance !== 'undefined' && typeof performance.now === 'function'
      ? performance.now()
      : 0;
  const hiResBits = Math.floor(hiResSubMs * 1_000_000) >>> 0;
  const mix = (t ^ (perfSimFallbackMixCounter * 0x9e3779b9) ^ hiResBits) >>> 0;
  return (mix % 0x1_0000_0000) / 0x1_0000_0000;
}

function warnPerfSimRandomCryptoFallbackOnce(message: string, error?: unknown): void {
  if (!isDevelopment || perfSimRandomCryptoFallbackLogged) {
    return;
  }
  perfSimRandomCryptoFallbackLogged = true;
  const detail =
    error === undefined ? '' : ` (${error instanceof Error ? error.message : String(error)})`;
  console.warn(`[${PERF_SIM_RANDOM_CRYPTO_FALLBACK}] ${message}${detail}`);
}

/**
 * Uniform float in [0, 1) for **simulated metric values only** (e.g. `forcePopulateTestData`).
 * Not for secrets or stable correlation IDs. Prefers `getRandomValues`; otherwise uses a
 * {@link weakNonCryptoUnitForSimulationFallback} that is safe for this **non-security** use only.
 */
function randomUnitForSimulation(): number {
  try {
    const g = globalThis as typeof globalThis & { crypto?: Crypto };
    const c = g.crypto ?? (typeof window !== 'undefined' ? window.crypto : undefined);
    if (c && typeof c.getRandomValues === 'function') {
      const buf = new Uint32Array(1);
      c.getRandomValues(buf);
      return buf[0] / 0x1_0000_0000;
    }
    warnPerfSimRandomCryptoFallbackOnce(
      'Web Crypto getRandomValues not available; using time-based mixing for simulated metrics only',
    );
  } catch (error: unknown) {
    warnPerfSimRandomCryptoFallbackOnce(
      'getRandomValues failed; using time-based mixing for simulated metrics only',
      error,
    );
  }
  return weakNonCryptoUnitForSimulationFallback();
}

interface PerformanceMetric {
  name: string;
  value: number;
  timestamp: number;
  metadata?: Record<string, any>;
}

interface NavigationTiming {
  navigationStart: number;
  htmlParsed: number;
  domReady: number;
  appMounted?: number;
  authComplete?: number;
  dataLoaded?: number;
}

interface ComponentMeasurement {
  id: string;
  componentName: string;
  startTime: number;
}

interface ComponentMetric {
  componentName: string;
  duration: number;
  timestamp: number;
}

class PerformanceMonitor {
  private static instance: PerformanceMonitor;
  private metrics: PerformanceMetric[] = [];
  private componentMetrics: ComponentMetric[] = [];
  private activeMeasurements: Map<string, ComponentMeasurement> = new Map();
  private observers: PerformanceObserver[] = [];
  private enabled: boolean = true;
  private navigationTiming: NavigationTiming = {
    navigationStart: 0,
    htmlParsed: 0,
    domReady: 0,
  };
  private lastComponentMetricTime: Map<string, number> = new Map();
  private componentMetricCooldown: number = 5000; // 5 second cooldown between metrics for same component

  static getInstance(): PerformanceMonitor {
    if (!PerformanceMonitor.instance) {
      PerformanceMonitor.instance = new PerformanceMonitor();
    }
    return PerformanceMonitor.instance;
  }

  private constructor() {
    // Only enable performance monitoring in development
    if (!isDevelopment) {
      this.enabled = false;
      return;
    }

    this.initializeNavigationTiming();
    this.setupObservers();
  }

  private initializeNavigationTiming(): void {
    if (!this.enabled) return;

    // Get timing data from the early performance script in index.html
    const marks = (window as any).performanceMarks || {};
    this.navigationTiming = {
      navigationStart: 0, // Always start from 0 for relative timing
      htmlParsed: marks.htmlParsed || performance.now(),
      domReady: marks.domReady || performance.now(),
    };

    console.log('Performance monitoring initialized:', this.navigationTiming);

    // Record initial timing metrics
    if (marks.htmlParsed) {
      this.recordMetric('HTMLParsed', marks.htmlParsed);
    }
    if (marks.domReady) {
      this.recordMetric('DOMReady', marks.domReady);
    }
  }

  private trySetupLCPObserver(supportedEntryTypes: readonly string[]): void {
    if (supportedEntryTypes.includes('largest-contentful-paint')) {
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const lastEntry = entries.length > 0 ? entries[entries.length - 1] : undefined;
          if (!lastEntry || !isLargestContentfulPaintEntry(lastEntry)) return;
          console.log('LCP captured:', lastEntry.startTime);
          this.recordMetric('LCP', lastEntry.startTime, {
            element: lastEntry.element?.tagName,
            url: lastEntry.url,
          });
        });
        lcpObserver.observe({ entryTypes: ['largest-contentful-paint'] });
        this.observers.push(lcpObserver);
        console.log('LCP observer setup successfully');
      } catch (error) {
        console.warn('Failed to setup LCP observer:', error);
      }
      return;
    }
    console.warn('LCP not supported in this browser');
    this.setupLCPFallback();
  }

  private trySetupFIDObserver(supportedEntryTypes: readonly string[]): void {
    if (!supportedEntryTypes.includes('first-input')) {
      console.warn('FID not supported in this browser');
      return;
    }
    try {
      const fidObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (!isFirstInputEntry(entry)) return;
          const fidValue = entry.processingStart - entry.startTime;
          console.log('FID captured:', fidValue);
          this.recordMetric('FID', fidValue, {
            eventType: entry.name,
          });
        });
      });
      fidObserver.observe({ entryTypes: ['first-input'] });
      this.observers.push(fidObserver);
      console.log('FID observer setup successfully');
    } catch (error) {
      console.warn('Failed to setup FID observer:', error);
    }
  }

  private trySetupCLSObserver(supportedEntryTypes: readonly string[]): void {
    if (!supportedEntryTypes.includes('layout-shift')) {
      console.warn('CLS not supported in this browser');
      return;
    }
    try {
      let cumulativeCLS = 0;
      const clsObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          if (isLayoutShiftEntry(entry) && !entry.hadRecentInput) {
            cumulativeCLS += entry.value;
          }
        });
        if (cumulativeCLS > 0) {
          console.log('CLS updated:', cumulativeCLS);
          this.recordMetric('CLS', cumulativeCLS);
        }
      });
      clsObserver.observe({ entryTypes: ['layout-shift'] });
      this.observers.push(clsObserver);
      console.log('CLS observer setup successfully');
    } catch (error) {
      console.warn('Failed to setup CLS observer:', error);
    }
  }

  private trySetupNavigationObserver(supportedEntryTypes: readonly string[]): void {
    if (!supportedEntryTypes.includes('navigation')) {
      console.warn('Navigation timing not supported in this browser');
      return;
    }
    try {
      const navigationObserver = new PerformanceObserver((list) => {
        const entries = list.getEntries();
        entries.forEach((entry) => {
          const navEntry = entry as PerformanceNavigationTiming;
          const ttfb = navEntry.responseStart - navEntry.requestStart;
          console.log('TTFB captured:', ttfb);
          this.recordMetric('TTFB', ttfb);
          this.recordMetric(
            'DOMContentLoaded',
            navEntry.domContentLoadedEventEnd - navEntry.fetchStart,
          );
          this.recordMetric('LoadComplete', navEntry.loadEventEnd - navEntry.fetchStart);
        });
      });
      navigationObserver.observe({ entryTypes: ['navigation'] });
      this.observers.push(navigationObserver);
      console.log('Navigation observer setup successfully');
    } catch (error) {
      console.warn('Failed to setup navigation observer:', error);
    }
  }

  private setupObservers(): void {
    if (!this.enabled) return;

    if (!('PerformanceObserver' in window)) {
      console.warn('PerformanceObserver not supported, using fallback methods');
      this.setupFallbackMethods();
      return;
    }

    try {
      const supportedEntryTypes = PerformanceObserver.supportedEntryTypes || [];
      console.log('Supported entry types:', supportedEntryTypes);

      this.trySetupLCPObserver(supportedEntryTypes);
      this.trySetupFIDObserver(supportedEntryTypes);
      this.trySetupCLSObserver(supportedEntryTypes);
      this.trySetupNavigationObserver(supportedEntryTypes);
    } catch (error) {
      console.warn('Failed to setup performance observers:', error);
    }
  }

  private setupLCPFallback(): void {
    // Try to get LCP from existing performance entries
    setTimeout(() => {
      try {
        const lcpEntries = performance.getEntriesByType('largest-contentful-paint');
        if (lcpEntries.length > 0) {
          const lastEntry = lcpEntries[lcpEntries.length - 1];
          if (lastEntry && isLargestContentfulPaintEntry(lastEntry)) {
            console.log('LCP fallback captured:', lastEntry.startTime);
            this.recordMetric('LCP', lastEntry.startTime, {
              element: lastEntry.element?.tagName,
              fallback: true,
            });
          }
        }
      } catch (error) {
        console.warn('LCP fallback failed:', error);
      }
    }, 2000); // Wait 2 seconds for LCP to be available
  }

  private setupFallbackMethods(): void {
    // Fallback methods for browsers without PerformanceObserver
    console.log('Setting up fallback performance monitoring');

    // Use setTimeout to periodically check for performance entries
    const checkInterval = setInterval(() => {
      try {
        // Check for LCP
        const lcpEntries = performance.getEntriesByType('largest-contentful-paint');
        if (lcpEntries.length > 0 && !this.getMetricsByName('LCP').length) {
          const lastEntry = lcpEntries[lcpEntries.length - 1];
          if (lastEntry && isLargestContentfulPaintEntry(lastEntry)) {
            this.recordMetric('LCP', lastEntry.startTime, { fallback: true });
          }
        }

        // Check for navigation timing
        const navEntries = performance.getEntriesByType('navigation');
        if (navEntries.length > 0 && !this.getMetricsByName('TTFB').length) {
          const navEntry = navEntries[0] as PerformanceNavigationTiming;
          const ttfb = navEntry.responseStart - navEntry.requestStart;
          this.recordMetric('TTFB', ttfb, { fallback: true });
        }

        // Check for layout shift entries
        const clsEntries = performance.getEntriesByType('layout-shift');
        if (clsEntries.length > 0 && !this.getMetricsByName('CLS').length) {
          let cumulativeCLS = 0;
          clsEntries.forEach((entry) => {
            if (isLayoutShiftEntry(entry) && !entry.hadRecentInput) {
              cumulativeCLS += entry.value;
            }
          });
          if (cumulativeCLS > 0) {
            this.recordMetric('CLS', cumulativeCLS, { fallback: true });
          }
        }

        // Stop checking after 10 seconds
        if (Date.now() - this.navigationTiming.navigationStart > 10000) {
          clearInterval(checkInterval);
        }
      } catch (error) {
        console.warn('Fallback performance check failed:', error);
      }
    }, 1000);

    // Clear interval after 10 seconds
    setTimeout(() => clearInterval(checkInterval), 10000);
  }

  recordMetric(name: string, value: number, metadata?: Record<string, any>): void {
    if (!this.enabled) return;

    console.log(`Recording metric: ${name} = ${value}ms`);

    const metric: PerformanceMetric = {
      name,
      value,
      timestamp: Date.now(),
      metadata,
    };

    // For critical metrics, replace existing values instead of accumulating
    const criticalMetrics = [
      'AuthCompleteTime',
      'DataLoadedTime',
      'TimeToInteractive',
      'AppMountTime',
      'LCP',
      'CLS',
      'FID',
      'TTFB',
    ];
    if (criticalMetrics.includes(name)) {
      // Remove existing metrics with the same name
      this.metrics = this.metrics.filter((m) => m.name !== name);
    }

    this.metrics.push(metric);

    // Keep only last 100 metrics to prevent memory leaks
    if (this.metrics.length > 100) {
      this.metrics = this.metrics.slice(-100);
    }

    // Clean up old metrics (older than 5 minutes)
    const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
    this.metrics = this.metrics.filter((m) => m.timestamp > fiveMinutesAgo);

    // Log significant performance issues
    this.checkThresholds(metric);
  }

  private checkThresholds(metric: PerformanceMetric): void {
    const thresholds = {
      LCP: 2500, // 2.5 seconds
      FID: 100, // 100ms
      CLS: 0.1, // 0.1
      TTFB: 600, // 600ms
    };

    const threshold = thresholds[metric.name as keyof typeof thresholds];
    if (threshold && metric.value > threshold) {
      console.warn(`Performance threshold exceeded for ${metric.name}:`, {
        value: metric.value,
        threshold,
        metadata: metric.metadata,
      });
    }
  }

  getMetrics(): PerformanceMetric[] {
    return [...this.metrics];
  }

  getMetricsByName(name: string): PerformanceMetric[] {
    return this.metrics.filter((metric) => metric.name === name);
  }

  getAverageMetric(name: string): number | null {
    const metrics = this.getMetricsByName(name);
    console.log(`Getting average for ${name}:`, metrics);
    if (metrics.length === 0) return null;

    const sum = metrics.reduce((acc, metric) => acc + metric.value, 0);
    const average = sum / metrics.length;
    console.log(`Average for ${name}:`, average);
    return average;
  }

  clearMetrics(): void {
    this.metrics = [];
    this.clearComponentMetrics();
  }

  // Clear critical metrics on app initialization
  clearCriticalMetrics(): void {
    console.log('Clearing critical metrics');
    const criticalMetrics = [
      'AuthCompleteTime',
      'DataLoadedTime',
      'TimeToInteractive',
      'AppMountTime',
    ];
    this.metrics = this.metrics.filter((m) => !criticalMetrics.includes(m.name));
    console.log('Remaining metrics after clear:', this.metrics.length);
  }

  // Clear all metrics to prevent accumulation
  clearAllMetrics(): void {
    this.metrics = [];
    this.componentMetrics = [];
    this.activeMeasurements.clear();
    this.lastComponentMetricTime.clear();
  }

  destroy(): void {
    this.observers.forEach((observer) => observer.disconnect());
    this.observers = [];
    this.clearMetrics();
  }

  // Application-specific timing methods
  markAppMounted(): void {
    // Only record if not already recorded
    if (!this.navigationTiming.appMounted) {
      this.navigationTiming.appMounted = performance.now();
      console.log('App mounted at:', this.navigationTiming.appMounted);
      this.recordMetric('AppMountTime', this.navigationTiming.appMounted);
    }
  }

  markAuthComplete(): void {
    // Only record if not already recorded
    if (!this.navigationTiming.authComplete) {
      this.navigationTiming.authComplete = performance.now();
      console.log('Auth complete at:', this.navigationTiming.authComplete);
      this.recordMetric('AuthCompleteTime', this.navigationTiming.authComplete);
    }
  }

  markDataLoaded(): void {
    // Only record if not already recorded
    if (!this.navigationTiming.dataLoaded) {
      this.navigationTiming.dataLoaded = performance.now();
      console.log('Data loaded at:', this.navigationTiming.dataLoaded);
      this.recordMetric('DataLoadedTime', this.navigationTiming.dataLoaded);

      // Calculate time to interactive (TTI approximation)
      // TTI should be when both auth and data are complete
      if (this.navigationTiming.authComplete) {
        const tti = Math.max(this.navigationTiming.dataLoaded, this.navigationTiming.authComplete);
        console.log('Time to Interactive:', tti);
        this.recordMetric('TimeToInteractive', tti);
      }
    }
  }

  getNavigationTiming(): NavigationTiming {
    return { ...this.navigationTiming };
  }

  // Resource loading performance
  trackResourceLoad(resourceName: string, startTime: number, endTime: number): void {
    const duration = endTime - startTime;
    this.recordMetric(`Resource_${resourceName}`, duration, {
      resourceType: 'asset',
      startTime,
      endTime,
    });
  }

  // API call performance
  trackApiCall(endpoint: string, method: string, duration: number, success: boolean): void {
    this.recordMetric(`API_${method}_${endpoint.replace(/[^a-zA-Z0-9]/g, '_')}`, duration, {
      endpoint,
      method,
      success,
      type: 'api',
    });
  }

  // Bundle loading performance
  trackBundleLoad(bundleName: string, size: number, duration: number): void {
    this.recordMetric(`Bundle_${bundleName}`, duration, {
      bundleName,
      size,
      type: 'bundle',
    });
  }

  // Enable/disable performance monitoring
  setEnabled(enabled: boolean): void {
    this.enabled = enabled;
  }

  setComponentMetricCooldown(cooldownMs: number): void {
    this.componentMetricCooldown = cooldownMs;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  isMonitoringEnabled(): boolean {
    return this.enabled;
  }

  // Component measurement methods
  startMeasure(componentName: string): string {
    if (!this.enabled) {
      return 'disabled';
    }

    // Correlation id for marks / maps (not a secret). UUID uses Web Crypto when available.
    const id = `${componentName}-${Date.now()}-${generateUuid()}`;
    let startTime: number;
    try {
      startTime =
        typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      if (typeof startTime !== 'number' || isNaN(startTime)) {
        startTime = Date.now();
      }
    } catch (error) {
      startTime = Date.now();
    }

    const measurement: ComponentMeasurement = {
      id,
      componentName,
      startTime,
    };

    this.activeMeasurements.set(id, measurement);

    // Also use browser's performance API if available
    if (typeof performance !== 'undefined' && performance.mark) {
      try {
        performance.mark(`${componentName}-start-${id}`);
      } catch (error) {
        console.warn(
          `Performance measurement failed: ${error instanceof Error ? error.message : String(error)}`,
        );
      }
    }

    return id;
  }

  private readSafePerformanceEndTime(): number {
    try {
      let endTime =
        typeof performance !== 'undefined' && performance.now ? performance.now() : Date.now();
      if (typeof endTime !== 'number' || isNaN(endTime)) {
        endTime = Date.now();
      }
      return endTime;
    } catch {
      return Date.now();
    }
  }

  private applyEndMeasurePerformanceApi(
    measureId: string,
    measurement: ComponentMeasurement,
  ): void {
    if (typeof performance === 'undefined' || !performance.mark || !performance.measure) {
      return;
    }
    try {
      const startMarkName = `${measurement.componentName}-start-${measureId}`;
      const endMarkName = `${measurement.componentName}-end-${measureId}`;
      const measureName = `${measurement.componentName}-measure-${measureId}`;

      performance.mark(endMarkName);
      performance.measure(measureName, startMarkName, endMarkName);

      performance.clearMarks(startMarkName);
      performance.clearMarks(endMarkName);
      performance.clearMeasures(measureName);
    } catch {
      // Ignore errors in performance API
    }
  }

  endMeasure(measureId: string, componentName?: string): number {
    if (!this.enabled || measureId === 'disabled') {
      return 0;
    }

    const measurement = this.activeMeasurements.get(measureId);
    if (!measurement) {
      console.warn('Performance measurement not found:', measureId);
      return 0;
    }

    try {
      const endTime = this.readSafePerformanceEndTime();
      let duration = endTime - measurement.startTime;

      if (duration <= 0) {
        duration = 0.1;
      }

      const finalComponentName = componentName || measurement.componentName;
      const now = Date.now();
      const lastTime = this.lastComponentMetricTime.get(finalComponentName) || 0;

      if (now - lastTime < this.componentMetricCooldown) {
        this.activeMeasurements.delete(measureId);
        return duration;
      }

      const componentMetric: ComponentMetric = {
        componentName: finalComponentName,
        duration,
        timestamp: now,
      };

      this.componentMetrics.push(componentMetric);
      this.lastComponentMetricTime.set(finalComponentName, now);

      if (this.componentMetrics.length > 100) {
        this.componentMetrics = this.componentMetrics.slice(-100);
      }

      const fiveMinutesAgo = Date.now() - 5 * 60 * 1000;
      this.componentMetrics = this.componentMetrics.filter((m) => m.timestamp > fiveMinutesAgo);

      this.activeMeasurements.delete(measureId);

      this.applyEndMeasurePerformanceApi(measureId, measurement);

      return duration;
    } catch (error) {
      console.warn(
        `Performance measurement failed: ${error instanceof Error ? error.message : String(error)}`,
      );
      return 0;
    }
  }

  getComponentMetrics(): ComponentMetric[] {
    return [...this.componentMetrics];
  }

  getAverageRenderTime(componentName: string): number {
    const componentMetrics = this.componentMetrics.filter((m) => m.componentName === componentName);

    if (componentMetrics.length === 0) return 0;

    const totalTime = componentMetrics.reduce((sum, metric) => {
      const duration = isNaN(metric.duration) ? 0 : metric.duration;
      return sum + duration;
    }, 0);

    const average = totalTime / componentMetrics.length;
    return isNaN(average) ? 0 : average;
  }

  clearComponentMetrics(): void {
    this.componentMetrics = [];
    this.activeMeasurements.clear();
  }

  // Generate performance report
  generatePerformanceReport(): Record<string, any> {
    // Try to get Core Web Vitals from Performance API if not captured by observers
    const lcp = this.getAverageMetric('LCP') || this.getLCPFromPerformanceAPI();
    const fid = this.getAverageMetric('FID') || this.getFIDFromPerformanceAPI();
    const cls = this.getAverageMetric('CLS') || this.getCLSFromPerformanceAPI();
    const ttfb = this.getAverageMetric('TTFB') || this.getTTFBFromPerformanceAPI();

    const report = {
      navigationTiming: this.navigationTiming,
      coreWebVitals: {
        LCP: lcp,
        FID: fid,
        CLS: cls,
        TTFB: ttfb,
      },
      applicationMetrics: {
        appMountTime: this.getAverageMetric('AppMountTime'),
        authCompleteTime: this.getAverageMetric('AuthCompleteTime'),
        dataLoadedTime: this.getAverageMetric('DataLoadedTime'),
        timeToInteractive: this.getAverageMetric('TimeToInteractive'),
      },
      componentMetrics: this.componentMetrics,
      resourceMetrics: this.metrics.filter((m) => m.metadata?.type === 'asset'),
      apiMetrics: this.metrics.filter((m) => m.metadata?.type === 'api'),
      bundleMetrics: this.metrics.filter((m) => m.metadata?.type === 'bundle'),
      timestamp: Date.now(),
    };

    return report;
  }

  // Fallback methods to get Core Web Vitals from Performance API
  private getLCPFromPerformanceAPI(): number | null {
    try {
      const entries = performance.getEntriesByType('largest-contentful-paint');
      if (entries.length > 0) {
        const lastEntry = entries[entries.length - 1];
        if (lastEntry && isLargestContentfulPaintEntry(lastEntry)) {
          return lastEntry.startTime;
        }
      }
    } catch (error) {
      console.warn('Failed to get LCP from Performance API:', error);
    }
    return null;
  }

  private getFIDFromPerformanceAPI(): number | null {
    try {
      const entries = performance.getEntriesByType('first-input');
      if (entries.length > 0) {
        const entry = entries[0];
        if (isFirstInputEntry(entry)) {
          return entry.processingStart - entry.startTime;
        }
      }
    } catch (error) {
      console.warn('Failed to get FID from Performance API:', error);
    }
    return null;
  }

  private getCLSFromPerformanceAPI(): number | null {
    try {
      const entries = performance.getEntriesByType('layout-shift');
      let clsValue = 0;
      entries.forEach((entry) => {
        if (isLayoutShiftEntry(entry) && !entry.hadRecentInput) {
          clsValue += entry.value;
        }
      });
      return clsValue > 0 ? clsValue : null;
    } catch (error) {
      console.warn('Failed to get CLS from Performance API:', error);
    }
    return null;
  }

  private getTTFBFromPerformanceAPI(): number | null {
    try {
      const entries = performance.getEntriesByType('navigation');
      if (entries.length > 0) {
        const entry = entries[0] as PerformanceNavigationTiming;
        return entry.responseStart - entry.requestStart;
      }
    } catch (error) {
      console.warn('Failed to get TTFB from Performance API:', error);
    }
    return null;
  }

  // Method to check if performance monitoring is working
  checkPerformanceSupport(): Record<string, boolean> {
    const support = {
      performanceObserver: 'PerformanceObserver' in window,
      performanceAPI: 'performance' in window,
      performanceNow: typeof performance?.now === 'function',
      performanceGetEntries: typeof performance?.getEntriesByType === 'function',
      largestContentfulPaint: false,
      firstInputDelay: false,
      cumulativeLayoutShift: false,
      navigationTiming: false,
    };

    if (support.performanceObserver) {
      const supportedTypes = PerformanceObserver.supportedEntryTypes || [];
      support.largestContentfulPaint = supportedTypes.includes('largest-contentful-paint');
      support.firstInputDelay = supportedTypes.includes('first-input');
      support.cumulativeLayoutShift = supportedTypes.includes('layout-shift');
      support.navigationTiming = supportedTypes.includes('navigation');
    }

    console.log('Performance API support:', support);
    return support;
  }

  // Method to completely reset the performance monitor
  resetPerformanceMonitor(): void {
    console.log('Resetting performance monitor to clean state');
    this.metrics = [];
    this.componentMetrics = [];
    this.activeMeasurements.clear();
    this.lastComponentMetricTime.clear();
    this.navigationTiming = {
      navigationStart: 0,
      htmlParsed: 0,
      domReady: 0,
      appMounted: undefined,
      authComplete: undefined,
      dataLoaded: undefined,
    };
  }

  // Method to force clear and populate with test data
  forcePopulateTestData(): void {
    console.log('=== FORCE POPULATING TEST DATA ===');

    // Completely reset first to ensure clean state
    this.resetPerformanceMonitor();

    // Force add Core Web Vitals with jittered test values (simulation only)
    const testLCP = 2000 + randomUnitForSimulation() * 1000; // 2-3 seconds
    const testCLS = 0.05 + randomUnitForSimulation() * 0.1; // 0.05-0.15
    const testFID = 50 + randomUnitForSimulation() * 100; // 50-150ms
    const testTTFB = 300 + randomUnitForSimulation() * 400; // 300-700ms

    this.recordMetric('LCP', testLCP, { simulated: true, forced: true });
    this.recordMetric('CLS', testCLS, { simulated: true, forced: true });
    this.recordMetric('FID', testFID, { simulated: true, forced: true });
    this.recordMetric('TTFB', testTTFB, { simulated: true, forced: true });

    // Force set application timing with jittered realistic ranges (simulation only)
    const testAppMountTime = 800 + randomUnitForSimulation() * 400; // 800-1200ms
    const testAuthCompleteTime = 1500 + randomUnitForSimulation() * 500; // 1500-2000ms
    const testDataLoadedTime = 2200 + randomUnitForSimulation() * 800; // 2200-3000ms
    const testTimeToInteractive = Math.max(testAuthCompleteTime, testDataLoadedTime) + 100; // Slightly after data loaded

    // Set navigation timing from simulated timings above
    this.navigationTiming.appMounted = testAppMountTime;
    this.navigationTiming.authComplete = testAuthCompleteTime;
    this.navigationTiming.dataLoaded = testDataLoadedTime;

    // Record the application metrics
    this.recordMetric('AppMountTime', testAppMountTime, { forced: true, simulated: true });
    this.recordMetric('AuthCompleteTime', testAuthCompleteTime, { forced: true, simulated: true });
    this.recordMetric('DataLoadedTime', testDataLoadedTime, { forced: true, simulated: true });
    this.recordMetric('TimeToInteractive', testTimeToInteractive, {
      forced: true,
      simulated: true,
    });

    console.log('Force populated metrics:', this.metrics);
    console.log('Navigation timing:', this.navigationTiming);
    console.log(
      'Test values - App Mount:',
      testAppMountTime,
      'Auth:',
      testAuthCompleteTime,
      'Data:',
      testDataLoadedTime,
      'TTI:',
      testTimeToInteractive,
    );
  }

  // Method to manually trigger performance measurements for testing
  triggerTestMeasurements(): void {
    console.log('=== TRIGGERING TEST MEASUREMENTS ===');
    console.log('Current metrics before test:', this.metrics.length);

    this.checkPerformanceSupport();

    // Always force populate for testing
    this.forcePopulateTestData();

    console.log('=== TEST MEASUREMENTS COMPLETED ===');
    console.log('Total metrics after test:', this.metrics.length);
    console.log('All metrics:', this.metrics);
    console.log('LCP metrics:', this.getMetricsByName('LCP'));
    console.log('CLS metrics:', this.getMetricsByName('CLS'));
    console.log('FID metrics:', this.getMetricsByName('FID'));
    console.log('TTFB metrics:', this.getMetricsByName('TTFB'));
  }

  // Utility method to measure function execution time
  async measureAsync<T>(name: string, fn: () => Promise<T>): Promise<T> {
    const start = performance.now();
    try {
      const result = await fn();
      const duration = performance.now() - start;
      this.recordMetric(name, duration);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      this.recordMetric(`${name}_error`, duration);
      throw error;
    }
  }

  measure<T>(name: string, fn: () => T): T {
    const start = performance.now();
    try {
      const result = fn();
      const duration = performance.now() - start;
      this.recordMetric(name, duration);
      return result;
    } catch (error) {
      const duration = performance.now() - start;
      this.recordMetric(`${name}_error`, duration);
      throw error;
    }
  }
}

export const performanceMonitor = PerformanceMonitor.getInstance();

// Add getMetrics method to the instance for backward compatibility
(performanceMonitor as any).getMetrics = () => performanceMonitor.getComponentMetrics();

// Expose performance monitor on window object for debugging (development only)
if (typeof window !== 'undefined') {
  (window as any).performanceMonitor = performanceMonitor;
  console.log('Performance monitor exposed on window.performanceMonitor for debugging');

  // Test the performance monitor immediately
  console.log('Testing performance monitor on initialization...');
  performanceMonitor.recordMetric('InitTest', 100, { test: true });
  console.log('Init test metric recorded');
}

// Export hook for React components
export const usePerformanceMonitor = (componentName?: string) => {
  return {
    startMeasure: (name?: string) =>
      performanceMonitor.startMeasure(name || componentName || 'Unknown'),
    endMeasure: (measureId: string, name?: string) =>
      performanceMonitor.endMeasure(measureId, name || componentName),
    recordMetric: (name: string, value: number, metadata?: Record<string, any>) =>
      performanceMonitor.recordMetric(name, value, metadata),
    measureAsync: <T>(name: string, fn: () => Promise<T>) =>
      performanceMonitor.measureAsync(name, fn),
    measure: <T>(name: string, fn: () => T) => performanceMonitor.measure(name, fn),
    getComponentMetrics: () => performanceMonitor.getComponentMetrics(),
    getAverageMetric: (name: string) => performanceMonitor.getAverageMetric(name),
    getAverageRenderTime: (name: string) => performanceMonitor.getAverageRenderTime(name),
    setEnabled: (enabled: boolean) => performanceMonitor.setEnabled(enabled),
    isEnabled: () => performanceMonitor.isEnabled(),
    setComponentMetricCooldown: (cooldownMs: number) =>
      performanceMonitor.setComponentMetricCooldown(cooldownMs),
    clearMetrics: () => performanceMonitor.clearMetrics(),
    clearCriticalMetrics: () => performanceMonitor.clearCriticalMetrics(),
    clearAllMetrics: () => performanceMonitor.clearAllMetrics(),
    markAppMounted: () => performanceMonitor.markAppMounted(),
    markAuthComplete: () => performanceMonitor.markAuthComplete(),
    markDataLoaded: () => performanceMonitor.markDataLoaded(),
    trackApiCall: (endpoint: string, method: string, duration: number, success: boolean) =>
      performanceMonitor.trackApiCall(endpoint, method, duration, success),
    trackResourceLoad: (resourceName: string, startTime: number, endTime: number) =>
      performanceMonitor.trackResourceLoad(resourceName, startTime, endTime),
    generateReport: () => performanceMonitor.generatePerformanceReport(),
    triggerTestMeasurements: () => performanceMonitor.triggerTestMeasurements(),
    forcePopulateTestData: () => performanceMonitor.forcePopulateTestData(),
    resetPerformanceMonitor: () => performanceMonitor.resetPerformanceMonitor(),
    checkPerformanceSupport: () => performanceMonitor.checkPerformanceSupport(),
  };
};
